import { Component, inject } from '@angular/core'
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidatorFn
} from '@angular/forms'
import { passwordPatternValidator } from '../../../../validators/validators'
import { Router, RouterModule } from '@angular/router'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { ApiService } from '../../../../services/api.service'
import { commonUtil } from '../../../../utils/common.util'

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.css',
  imports: [ReactiveFormsModule, TranslatePipe, RouterModule]
})
export class ForgotPassword {
  formBuilder = inject(FormBuilder)
  router = inject(Router)
  apiService = inject(ApiService)
  translateService = inject(TranslateService)
  isSendingCode = false
  isResetting = false
  currentStep = 1
  countdown = 90
  countdownInterval: any = null
  email: string = ''

  isShowPwd = this.formBuilder.control(false)
  isShowConfirmPwd = this.formBuilder.control(false)

  passwordMatchValidator: ValidatorFn = (
    control: AbstractControl
  ): { [key: string]: any } | null => {
    const password = control.get('newPassword')
    const confirmPassword = control.get('confirmPassword')

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true })
      return { passwordMismatch: true }
    }
    return null
  }

  forgotPasswordForm = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]]
  })

  resetPasswordForm = this.formBuilder.group(
    {
      code: ['', [Validators.required]],
      newPassword: ['', [Validators.required, passwordPatternValidator()]],
      confirmPassword: ['', [Validators.required]]
    },
    {
      validators: this.passwordMatchValidator
    }
  )

  get emailControl(): AbstractControl | null {
    return this.forgotPasswordForm.get('email')
  }

  get code(): AbstractControl | null {
    return this.resetPasswordForm.get('code')
  }

  get newPassword(): AbstractControl | null {
    return this.resetPasswordForm.get('newPassword')
  }

  get confirmPassword(): AbstractControl | null {
    return this.resetPasswordForm.get('confirmPassword')
  }

  onSendCode() {
    if (this.forgotPasswordForm.invalid) {
      this.forgotPasswordForm.markAllAsTouched()
      return
    }

    const emailValue = this.emailControl?.value
    if (!emailValue) {
      return
    }

    this.isSendingCode = true
    this.apiService.sendResetPasswordCode(emailValue).subscribe({
      next: (res) => {
        this.isSendingCode = false
        if (res.status === 'ok') {
          this.email = emailValue
          this.currentStep = 2
          this.startCountdown()
        }
      },
      error: (err) => {
        this.isSendingCode = false
      }
    })
  }

  startCountdown() {
    this.countdown = 90
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
    this.countdownInterval = setInterval(() => {
      this.countdown--
      if (this.countdown <= 0) {
        clearInterval(this.countdownInterval)
      }
    }, 1000)
  }

  onResendCode() {
    if (this.countdown > 0) {
      return
    }

    if (!this.email) {
      return
    }

    this.isSendingCode = true
    this.apiService.sendResetPasswordCode(this.email).subscribe({
      next: (res) => {
        this.isSendingCode = false
        if (res.status === 'ok') {
          commonUtil.alert(
            this.translateService.instant('app.forgotPassword.codeSent'),
            'success'
          )
          this.startCountdown()
        }
      },
      error: (err) => {
        this.isSendingCode = false
      }
    })
  }

  onResetPassword() {
    if (this.resetPasswordForm.invalid) {
      this.resetPasswordForm.markAllAsTouched()
      return
    }

    const code = this.code?.value
    const newPassword = this.newPassword?.value
    const confirmPassword = this.confirmPassword?.value

    if (!code || !newPassword || !confirmPassword) {
      return
    }

    if (newPassword !== confirmPassword) {
      commonUtil.alert(
        this.translateService.instant('app.forgotPassword.passwordsDoNotMatch'),
        'danger'
      )
      return
    }

    this.isResetting = true
    this.apiService
      .resetPassword(this.email, code, newPassword, confirmPassword)
      .subscribe({
        next: (res) => {
          this.isResetting = false
          if (res.status === 'ok') {
            commonUtil.alert(
              this.translateService.instant('app.forgotPassword.passwordUpdatedSuccessfully'),
              'success'
            )
            this.router.navigate(['/auth/login'])
          }
        },
        error: (err) => {
          this.isResetting = false
        }
      })
  }

  onBackToStep1() {
    this.currentStep = 1
    this.email = ''
    this.resetPasswordForm.reset()
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
  }
}